import {z} from 'zod';
import {storedCongestionWindowSchema, storedCongestionWindowsSchema} from './congestion-window';
import {
  congestionAreaIdSchema,
  organizationIdSchema,
  previewIdSchema,
  requestIdSchema,
  submissionIdSchema,
  userIdSchema,
} from './id';

/**
 * The request machine, in the order `docs/state-machine.md` lists it. Twelve states, of
 * which everything from `PUBLISHING` onward is derived from the message rows rather than
 * set by hand.
 *
 * Enumerated columns are `varchar` + `CHECK` rather than `pgEnum` — ADR 0011 — so this
 * tuple is what the CHECK constraint is built from. Renaming a value here without a
 * migration makes the column refuse the new name.
 */
export const REQUEST_STATUSES = [
  'WAITING_FOR_APPROVAL',
  'DISAPPROVED',
  'CANCELLED',
  'READY_FOR_PUBLISHING',
  'PUBLISHING',
  'PUBLISHED',
  'PARTIALLY_PUBLISHED',
  'PUBLISHING_FAILED',
  'ACKNOWLEDGED',
  'PARTIALLY_ACKNOWLEDGED',
  'NOT_ACKNOWLEDGED',
  'EXPIRED',
] as const;

export type RequestStatus = (typeof REQUEST_STATUSES)[number];

export const requestStatusSchema = z.enum(REQUEST_STATUSES);

/**
 * One message is one `FLEX` event for one resource group. Several names are shared with
 * the request statuses and mean something narrower here: on a message they describe one
 * resource group, on the request they are the aggregate. Separate enums, separate columns.
 */
export const MESSAGE_STATUSES = [
  /**
   * Built and reviewable, but not queued. Events are constructed when the request is
   * approved, so the approver's decision covers the bytes that will be sent rather than a
   * profile that becomes them later. Pressing publish is what turns these into `PENDING`.
   *
   * A separate status rather than an early `PENDING`, because `PENDING` means "the sweep
   * may send this": the expiry sweep would otherwise close these as `NEVER_SENT` — a page
   * — and the aggregate would recompute the request into `PUBLISHING` with nobody having
   * pressed anything.
   */
  'WAITING_FOR_PUBLISH',
  'PENDING',
  'SENDING',
  'PUBLISHED',
  'PUBLISHING_FAILED',
  'ACKNOWLEDGED',
  'NOT_ACKNOWLEDGED',
] as const;

export type MessageStatus = (typeof MESSAGE_STATUSES)[number];

export const messageStatusSchema = z.enum(MESSAGE_STATUSES);

/**
 * Why a message is `PUBLISHING_FAILED`. `REFUSED` and `NEVER_SENT` are facts about the
 * world; `UNCONFIRMED` says only that the last read found no event, which is not the same
 * as there being none — hence the one edge out of a failure state.
 */
export const FAILURE_REASONS = ['REFUSED', 'NEVER_SENT', 'UNCONFIRMED'] as const;

export type FailureReason = (typeof FAILURE_REASONS)[number];

export const failureReasonSchema = z.enum(FAILURE_REASONS);

/**
 * The states that release a congestion area's day: refused, withdrawn, or never sent.
 * `PUBLISHING_FAILED` is deliberately absent — an `UNCONFIRMED` message may still surface
 * at Callie, and a released slot plus a late event puts two dispatches on one group.
 */
const DAY_RELEASING_STATUSES = ['DISAPPROVED', 'CANCELLED', 'EXPIRED'] as const satisfies readonly RequestStatus[];

type DayReleasingStatus = (typeof DAY_RELEASING_STATUSES)[number];

/**
 * The predicate of the partial unique index over (congestion_area_id, dispatch_day) —
 * ADR 0012. Derived rather than written out so a new request status occupies the day by
 * default: a state that should release it has to say so above, where the reasoning is.
 */
export const SLOT_OCCUPYING_STATUSES = REQUEST_STATUSES.filter(
  (status): status is Exclude<RequestStatus, DayReleasingStatus> =>
    !(DAY_RELEASING_STATUSES as readonly RequestStatus[]).includes(status),
);

export type SlotOccupyingStatus = (typeof SLOT_OCCUPYING_STATUSES)[number];

/**
 * A calendar day in `Europe/Amsterdam`, not an instant. Stored as `date` so the day a
 * dispatch covers cannot shift with a timezone conversion, and matched rather than
 * coerced: an ISO timestamp reaching this field means somebody converted too early.
 *
 * `z.iso.date()` rather than a `YYYY-MM-DD` pattern, because a pattern accepts days the
 * calendar has not got. `2026-02-30` would then travel the whole write path and be
 * refused by Postgres, turning a validation error into a 500 at the last step.
 */
export const dispatchDaySchema = z.iso.date();

/**
 * A request as every list and detail read returns it.
 *
 * `profile` is deliberately absent. It is Solvie's output, frozen at submit, and only the
 * publish path and the detail view have any use for it — leaving it out of the row type
 * means a list query cannot be asked for it by accident. No column holds the publication
 * deadline either: it is derived from `dispatchDay`, so a stored copy could drift from the
 * rule that produced it.
 */
export const requestSchema = z.object({
  id: requestIdSchema,
  organizationId: organizationIdSchema,
  congestionAreaId: congestionAreaIdSchema,
  dispatchDay: dispatchDaySchema,
  status: requestStatusSchema,
  statusChangedAt: z.coerce.date(),
  /** The wizard run this came from; several requests may share it. */
  submissionId: submissionIdSchema,
  previewId: previewIdSchema,
  /**
   * The resource groups Groupie served when the profile was calculated, as
   * `{id, name}` — Solvie's curves are keyed by the group's id and carry no name, so
   * without this every chart and every table would have to show a bare uuid.
   *
   * Captured at preview rather than read again later: it is what the requester and the
   * approver were looking at, and a group renamed since must not change what this request
   * says it dispatched.
   */
  resourceGroups: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      /**
       * The group's `ACTIVE_AVAILABLE_FLEX` in kW when the profile was calculated, which
       * is what the requested percentage was a percentage *of*. Carried so a chart can
       * scale to "all of the flexibility on offer" rather than to the request's own peak,
       * and two requests can be compared by eye.
       *
       * Optional because requests submitted before it was captured do not have it.
       */
      availableFlexKw: z.number().nonnegative().optional(),
      /**
       * The two attributes an event is built from, frozen with the rest at preview:
       * `targets` is what a `FLEX` event steers — exactly one, rule 2 — and `maxDuration`
       * is how long the group may be held.
       *
       * **Stored so approval needs no upstream call.** Groupie offers no filter by
       * congestion point, so reading the groups means walking its whole collection: 820
       * groups over 17 pages, eight to ten seconds an area, inside a lambda with a 30
       * second ceiling. An approval that depends on that fails for reasons the approver
       * cannot see or fix.
       *
       * Optional because a request submitted before this shipped carries neither, and
       * `eventsFor` falls back to reading Groupie for those.
       */
      targets: z.array(z.string()).optional(),
      maxDuration: z.string().optional(),
    }),
  ),
  /**
   * The congestion windows the wizard asked for, frozen with the profile.
   *
   * They are not decoration: every interval of every event this request publishes is one of
   * them, and its `FLEX` payload carries a value per quarter of it (ADR 0034). Stored rather
   * than taken from the profile's echoed `congestion_windows`, whose timezone is an open
   * question (`docs/open-questions.md` §12).
   *
   * **Empty on a request submitted before they were stored.** Such a request cannot be
   * published — `buildFlexEvents` refuses a profile with nowhere to put it — which is the
   * honest outcome: there is no record of what was asked for, and it is not ours to guess.
   */
  windows: storedCongestionWindowsSchema.or(z.array(storedCongestionWindowSchema).max(0)),
  createdBy: userIdSchema,
  decidedBy: userIdSchema.nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type Request = z.infer<typeof requestSchema>;

/**
 * A row of the list, which is a request plus the one thing every screen shows next to it:
 * the name of the area it dispatches. Joined server-side rather than resolved in the
 * browser — a list of ids the frontend has to look up is a second request and a second
 * chance to show the wrong name.
 */
export const requestSummarySchema = requestSchema.extend({
  congestionAreaName: z.string(),
});

export type RequestSummary = z.infer<typeof requestSummarySchema>;

/**
 * The states that cannot change on their own, from `docs/state-machine.md`.
 *
 * Here rather than in the frontend because it is a fact about the machine, not a drawing
 * decision: `PUBLISHED` is not terminal because reports keep arriving, and a screen that
 * decided otherwise would be re-deciding the state machine.
 */
export const TERMINAL_REQUEST_STATUSES = [
  'DISAPPROVED',
  'CANCELLED',
  'PARTIALLY_PUBLISHED',
  'PUBLISHING_FAILED',
  'ACKNOWLEDGED',
  'PARTIALLY_ACKNOWLEDGED',
  'NOT_ACKNOWLEDGED',
  'EXPIRED',
] as const satisfies readonly RequestStatus[];

export const isTerminalRequestStatus = (status: RequestStatus): boolean =>
  (TERMINAL_REQUEST_STATUSES as readonly RequestStatus[]).includes(status);

export const TERMINAL_MESSAGE_STATUSES = [
  'PUBLISHING_FAILED',
  'ACKNOWLEDGED',
  'NOT_ACKNOWLEDGED',
] as const satisfies readonly MessageStatus[];

export const isTerminalMessageStatus = (status: MessageStatus): boolean =>
  (TERMINAL_MESSAGE_STATUSES as readonly MessageStatus[]).includes(status);

/**
 * States that exist for the machine rather than for the reader: they resolve within one
 * sweep interval and say nothing to a customer beyond "being sent". An operator view
 * shows them as they are; a customer view may fold them into the state before.
 *
 * A judgement about the domain, so it lives with the domain — a screen should not have to
 * work out which states are its business.
 */
export const OPERATOR_ONLY_REQUEST_STATUSES = ['PUBLISHING'] as const satisfies readonly RequestStatus[];

export const OPERATOR_ONLY_MESSAGE_STATUSES = ['PENDING', 'SENDING'] as const satisfies readonly MessageStatus[];

export const isCustomerFacingRequestStatus = (status: RequestStatus): boolean =>
  !(OPERATOR_ONLY_REQUEST_STATUSES as readonly RequestStatus[]).includes(status);

export const isCustomerFacingMessageStatus = (status: MessageStatus): boolean =>
  !(OPERATOR_ONLY_MESSAGE_STATUSES as readonly MessageStatus[]).includes(status);
